import {
  BlinkIdSingleSideRecognizerResult,
  DisplayableQuad,
  IdBarcodeRecognizerResult,
  ImageAnalysisDetectionStatus,
  ProcessingStatus,
  RecognizerResultState,
} from '@microblink/blinkid-in-browser-sdk';
import { Point } from '@microblink/blinkid-in-browser-sdk/types/MicroblinkSDK/Geometry';
import { MrzResult } from '@microblink/blinkid-in-browser-sdk/types/Recognizers/BlinkID/MRTD/MrtdStructures';
import { RefObject, useRef, useState } from 'react';
import { useAsyncEffect } from 'use-async-effect';

import type {
  AutoCapturePillPassthroughProps,
  ScannerHelpModalPassthroughProps,
  ScannerOverlayPassthroughProps,
} from 'inquiry/lib/types';
import BaseScanner from 'inquiry/scanners/BaseScanner/BaseScanner';
import {
  backIsIncomplete,
  DETECT_INTERVAL,
  detectsGlare,
  FULL_BACK_SIDE_TRIGGER_CONFIGS,
  GovernmentIdMicroblinkTriggerParams,
  GovernmentIdMicroblinkTriggerType,
  hasValidMrz,
  hasValidQuad,
  notCentered,
  PROCESSING_TRIGGER,
  tooClose,
  tooFar,
  TRIGGER_CONFIGS_BY_SIDE,
} from 'inquiry/scanners/GovernmentIdMicroblinkScanner/client';
import EventsClient from 'lib/clients/EventsClient';
import FrameTrigger from 'lib/clients/FrameTrigger';
import FrameTriggerClient from 'lib/clients/FrameTriggerClient';
import LivePhotoCaptureClient, {
  IMAGE_FORMAT,
  IMAGE_QUALITY,
} from 'lib/clients/LivePhotoCaptureClient';
import {
  countryToString,
  documentTypeToString,
  MicroblinkClient as MicroblinkClientNew,
  regionToString,
} from 'lib/clients/MicroblinkClient';
import { MicroblinkClient as MicroblinkClientOld } from 'lib/clients/MicroblinkClientOld';
import { GovernmentIdClass } from 'lib/constants/governmentIds';
import useInquiryFeatureFlag from 'lib/hooks/useInquiryFeatureFlag';
import { StepGovernmentIdConfig } from 'lib/interfaces/StepsConfig';
import { ButtonComponentStyles } from 'lib/interfaces/ui-components';
import { GovernmentIdOverlay, GovernmentIdSide } from 'lib/interfaces/verificationConfig';
import { CameraInspectionData } from 'verify/lib/interfaces/cameraInspection';
import { calculateLuminosity, Rect } from 'verify/lib/utils/image';
import { bufferImageWithOverlayWindow } from 'verify/lib/utils/webcam';
import { CaptureMethod, ClientExtraction } from 'verify/models/verification';
import { SelectedCamera } from 'verify/scanners/GovernmentIdMicroblinkScanner/MicroblinkCameraUtils';

const DEFAULT_MANUAL_CAPTURE_DELAY = 8000;
/**
 * Classification is faster/less resource intensive than extraction. On some
 * devices extraction takes way too long, so we early exit and use the image as
 * long as classification succeeded.
 *
 * Note that any blur, glare, etc. checks are ignored.
 */
const SKIP_EXTRACTION_DELAY = 1000;
interface GovernmentIdMicroblinkScannerProps {
  setAutoCaptureReady: () => void;
  delayUntilManualCaptureMs?: number;
  handleCapture: (
    imageDataUris: string[],
    captureMethod: CaptureMethod,
    cameraInspectionData: CameraInspectionData,
    clientExtractions?: ClientExtraction[],
    clientExtractionSource?: string,
  ) => Promise<void>;
  /**
   * Logs an alert and instructs the parent to use GovernmentIdScanner as a
   * fallback instead of MicroblinkScanner.
   */
  handleError: (err: Error | string, state: 'load' | 'capture') => void;
  labelTakePhoto: string;
  renderOverlay?: (props: ScannerOverlayPassthroughProps) => JSX.Element;
  videoConstraints: MediaTrackConstraints;
  requiresSide: GovernmentIdSide;
  governmentIdClass?: GovernmentIdClass;
  reportDebugMetrics?: boolean;
  handleTrigger: (
    trigger: FrameTrigger<
      unknown,
      unknown,
      StepGovernmentIdConfig['localizations']['capture-page']
    >,
  ) => void;
  handleCameraChange: (selectedCamera: SelectedCamera) => void;
  selectedCamera?: string;
  cameraSelectOptions: SelectedCamera[];
  shouldDisplayCameraSelector: boolean;
  overlayReady: boolean;
  overlayRef: RefObject<HTMLDivElement>;
  overlayCaptureWindowRef: RefObject<HTMLDivElement>;
  previewViewfinderRef: RefObject<HTMLDivElement>;
  requireFullBack: boolean;
  allowIncompleteBarcode: boolean;
  isShortBarcodeOverlay: boolean;
  submitButtonStyles?: ButtonComponentStyles;
  showHelpButton?: boolean;
  helpButtonIconColor?: string;
  helpButtonLabel?: string;
  renderAutoCapturePill?: (props: AutoCapturePillPassthroughProps) => JSX.Element;
  renderHelpModal?: (props: ScannerHelpModalPassthroughProps) => JSX.Element;

  uiReady: boolean;
  videoReady: boolean;

  // additional props (in addition to requiresSide and governmentIdSide) to pass to BaseScanner to render overlay inline
  assets: StepGovernmentIdConfig['assets']['capture-page'];
  captureOverlay?: GovernmentIdOverlay | null;
  captureFrameLabel: string;
  hint?: string | null;
  prompt?: string | null;
}

/**
 * GovernmentIdMicroblinkScanner is for when we want to capture using Microblink
 */
const GovernmentIdMicroblinkScanner = ({
  setAutoCaptureReady,
  /**
   * Manual capture was always enabled for GovernmentIdMicroblinkScanner before this prop was implemented.
   */
  delayUntilManualCaptureMs = DEFAULT_MANUAL_CAPTURE_DELAY,
  handleCapture,
  labelTakePhoto,
  renderOverlay,
  videoConstraints,
  requiresSide,
  governmentIdClass,
  handleError,
  reportDebugMetrics = false,
  handleTrigger,
  handleCameraChange,
  selectedCamera,
  cameraSelectOptions,
  shouldDisplayCameraSelector,
  overlayReady,
  overlayRef,
  overlayCaptureWindowRef,
  previewViewfinderRef,
  requireFullBack = false,
  allowIncompleteBarcode = false,
  isShortBarcodeOverlay,
  submitButtonStyles,
  showHelpButton,
  helpButtonIconColor,
  helpButtonLabel,
  renderAutoCapturePill,
  renderHelpModal,
  uiReady,
  videoReady,
  // additional props (in addition to requiresSide and governmentIdSide) to pass to BaseScanner to render overlay inline
  assets,
  captureOverlay,
  captureFrameLabel,
  hint,
  prompt,
}: GovernmentIdMicroblinkScannerProps) => {
  let initializationTime: number | undefined = undefined;
  const isCaptured = useRef(false);
  const instanceNumber = useRef<number | undefined>(undefined);
  const [isSlowProcessing, setIsSlowProcessing] = useState(false);
  const enableLowLightDetection = useInquiryFeatureFlag('inquiry:enable-low-light-detection-web');

  const useOldMicroblink = useInquiryFeatureFlag('inquiry:use-old-microblink');

  const MicroblinkClient = useOldMicroblink ? MicroblinkClientOld : MicroblinkClientNew;

  useAsyncEffect(
    async (isMounted) => {
      try {
        const mountTime = performance.now();

        // keep track of the current instance number to prevent race conditions
        // between tear down and initialize
        // this happens when pressing back in govid redesign, since we go from
        // one capture view to another capture view without anything in between
        // in non-govid redesign this is not necessary since we never go directly
        // from one microblink scanner to a different one
        instanceNumber.current = await MicroblinkClient.initialize(
          requiresSide,
          governmentIdClass,
          {
            requireFullBack,
            allowIncompleteBarcode,
          },
        );
        initializationTime = performance.now();
        if (reportDebugMetrics) {
          EventsClient.logAmplitude('government-id.microblink.initialization', {
            initializationTime: (initializationTime - mountTime) / 1000,
            side: requiresSide,
            idClass: governmentIdClass,
          });
        }

        if (isMounted()) {
          setAutoCaptureReady();
        } else {
          await MicroblinkClient.tearDownInstance(instanceNumber.current);
          return;
        }
      } catch (err) {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        handleError(err, 'load');
      }
    },
    () => {
      // only perform tear down if another microblink scanner hasn't been initiated
      MicroblinkClient.tearDownInstance(instanceNumber.current);
      initializationTime = undefined;
    },
    [requiresSide, requireFullBack],
  );

  /**
   * Called by runTriggerClient on every tick and when manually capturing.
   * Passes the canvas to microblink and tries to extract via `processImage`.
   * Based on the required side, we pass different params to the caller.
   *
   * Contains an optimization that submits a frame for capture if it passes
   * classification but not extraction after a timeout for low-end devices where
   * extraction takes too long.
   *
   * @returns params passed to FrameTriggerClient, which powers capture hints.
   */
  const paramsFromVideoFrame = async (
    videoElement: HTMLVideoElement,
    canvasBuffer: HTMLCanvasElement,
    _captureClient: LivePhotoCaptureClient | null,
    triggerClient: FrameTriggerClient<
      GovernmentIdMicroblinkTriggerType,
      GovernmentIdMicroblinkTriggerParams,
      StepGovernmentIdConfig['localizations']['capture-page']
    >,
    viewfinderRegionInCanvas: Rect,
  ): Promise<GovernmentIdMicroblinkTriggerParams> => {
    bufferImageWithOverlayWindow({
      source: videoElement,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      overlayCaptureWindowElement: overlayCaptureWindowRef.current,
      canvasBuffer,
    });

    const videoWidth = canvasBuffer.width;
    const videoHeight = canvasBuffer.height;
    try {
      const imageDataUri = canvasBuffer.toDataURL(IMAGE_FORMAT, IMAGE_QUALITY);
      const start = performance.now();
      let isProcessing = true;
      setIsSlowProcessing(false);
      setTimeout(() => {
        if (isProcessing) {
          setIsSlowProcessing(true);
        }
      }, 1000);

      /**
       * This is an optimization for lower-end devices, where recognition is
       * fast but extraction is slow. We don't _need_ the extraction from the
       * client, but it's nice to have, so early exit after 1 second.
       */
      const onClassifiedCallback = (supportedClassification: boolean) => {
        if (supportedClassification) {
          setTimeout(() => {
            if (isProcessing && requiresSide === GovernmentIdSide.Front) {
              const end = performance.now();
              const timing = end - start;

              handleMicroblinkCapture(
                {
                  currentTrigger: triggerClient.getTriggered().getType(),
                  detectedQuad: null,
                  microblinkResult: null,
                  luminosityData: enableLowLightDetection
                    ? calculateLuminosity(canvasBuffer, viewfinderRegionInCanvas)
                    : null,
                  videoWidth,
                  videoHeight,
                  imageDataUri,
                  hasGlare: null,
                  processingTime: timing,
                  isShortBarcodeOverlay,
                },
                CaptureMethod.Auto,
                null,
              );
              isProcessing = false;
            }
          }, SKIP_EXTRACTION_DELAY);
        }
      };

      const processedImage = await MicroblinkClient.processImage(
        canvasBuffer,
        onClassifiedCallback,
      );

      isProcessing = false;
      if (requiresSide === GovernmentIdSide.Front) {
        const end = performance.now();
        const timing = end - start;

        const params: GovernmentIdMicroblinkTriggerParams = {
          currentTrigger: triggerClient.getTriggered().getType(),
          detectedQuad: processedImage?.detectedQuad ?? null,
          hasGlare: processedImage?.hasGlare ?? null,
          microblinkRecognizerName: processedImage?.recognizerName,
          microblinkResult: processedImage?.recognitionResult as BlinkIdSingleSideRecognizerResult,
          luminosityData: enableLowLightDetection
            ? calculateLuminosity(canvasBuffer, viewfinderRegionInCanvas)
            : null,
          videoWidth,
          videoHeight,
          imageDataUri,
          processingTime: timing,
          isShortBarcodeOverlay,
        };
        return params;
      } else if (
        requiresSide === GovernmentIdSide.BarcodePdf417 ||
        requiresSide === GovernmentIdSide.Back
      ) {
        const end = performance.now();
        const timing = end - start;

        const params: GovernmentIdMicroblinkTriggerParams = {
          currentTrigger: triggerClient.getTriggered().getType(),
          detectedQuad: processedImage?.detectedQuad ?? null,
          microblinkRecognizerName: processedImage?.recognizerName,
          microblinkResult: processedImage?.recognitionResult as IdBarcodeRecognizerResult,
          luminosityData: enableLowLightDetection
            ? calculateLuminosity(canvasBuffer, viewfinderRegionInCanvas)
            : null,
          videoWidth,
          videoHeight,
          hasGlare: processedImage?.hasGlare ?? null,
          imageDataUri,
          processingTime: timing,
          hasIncompleteBack: processedImage?.hasIncompleteBack ?? false,
          isShortBarcodeOverlay,
        };
        return params;
      }
    } catch (err) {
      // Here, we stop tick'ing but do nothing else because they can manually take a photo.
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      handleError(err, 'capture');
    }

    // Empty fallback
    const params: GovernmentIdMicroblinkTriggerParams = {
      currentTrigger: triggerClient.getTriggered().getType(),
      luminosityData: enableLowLightDetection
        ? calculateLuminosity(canvasBuffer, viewfinderRegionInCanvas)
        : null,
      videoWidth,
      videoHeight,
      microblinkRecognizerName: null,
      detectedQuad: null,
      microblinkResult: null,
      hasGlare: null,
      imageDataUri: null,
      processingTime: null,
      isShortBarcodeOverlay,
    };
    return params;
  };

  /**
   * Determines if autocapture was valid and if we should call
   * `handleMicroblinkCapture`.
   */
  const shouldSubmit = (
    params: GovernmentIdMicroblinkTriggerParams,
    _triggerClient: FrameTriggerClient<
      GovernmentIdMicroblinkTriggerType,
      GovernmentIdMicroblinkTriggerParams,
      StepGovernmentIdConfig['localizations']['capture-page']
    >,
  ): boolean => {
    // if we allow incomplete barcode extraction, accept the result if the document
    // quads are detected and a barcode is detected (but not extracted from)
    // TODO: Consider moving allowIncompleteBarcode handling into params
    // creation and store in params as a boolean attribute.
    // (e.g. `params.sideSpecificResult`)
    const recognizerWasSuccessful =
      params.microblinkResult?.state === RecognizerResultState.Valid ||
      (requiresSide === GovernmentIdSide.Back &&
        allowIncompleteBarcode &&
        params.microblinkResult?.state === RecognizerResultState.Uncertain &&
        (params.microblinkResult as BlinkIdSingleSideRecognizerResult).imageAnalysisResult
          ?.barcodeDetectionStatus === ImageAnalysisDetectionStatus.Detected);

    const processingStatus = (params.microblinkResult as BlinkIdSingleSideRecognizerResult)
      ?.processingStatus;
    // Arguably ANYTHING besides Success should be rejected, but err on the side of caution now.
    // From manual testing, failing these two strongly suggest the captured image is unusable.
    // NOTE: processingStatus will be null if we early exit via onClassifiedCallback.
    // NOTE: the barcode recognizer does not support processingStatus.
    const processingWasSuccessful =
      // Happens on blur
      processingStatus !== ProcessingStatus.ImagePreprocessingFailed &&
      // Happens when processing is slow and inconsistent information is extracted across captures
      processingStatus !== ProcessingStatus.StabilityTestFailed;

    // We manually handle the submission to ensure we only submit successfully parsed results
    const shouldSubmit =
      recognizerWasSuccessful &&
      processingWasSuccessful &&
      !tooFar(requiresSide, params, requireFullBack) &&
      !tooClose(requiresSide, params, requireFullBack) &&
      !notCentered(params) &&
      !backIsIncomplete(requiresSide, params, requireFullBack) &&
      !detectsGlare(params) &&
      hasValidMrz(governmentIdClass, params);

    // Debug logging to make diagnosing autocapture issues easier
    if (!shouldSubmit) {
      if (params?.detectedQuad == null) {
        console.info('Autocapture failed: no ID detected');
      } else {
        const failureReasons = [
          (!recognizerWasSuccessful || !processingWasSuccessful) && 'Failed to recognize ID',
          tooFar(requiresSide, params, requireFullBack) && 'ID too far',
          tooClose(requiresSide, params, requireFullBack) && 'ID too close',
          notCentered(params) && 'ID not centered',
          backIsIncomplete(requiresSide, params, requireFullBack) && 'Back is incomplete',
          detectsGlare(params) && 'Has glare',
          !hasValidMrz(governmentIdClass, params) && 'Missing MRZ',
        ].filter(Boolean);
        console.info(`Autocapture failed: ${failureReasons.join(', ') || 'Reason unknown'}`);
      }
    }

    return shouldSubmit;
  };

  const normalizePoint = (point: Point, videoWidth: number, videoHeight: number) => {
    return {
      x: point.x / videoWidth,
      y: point.y / videoHeight,
    };
  };

  const normalizeQuad = (quad: DisplayableQuad, videoWidth: number, videoHeight: number) => {
    return {
      bottomLeft: normalizePoint(quad.bottomLeft, videoWidth, videoHeight),
      bottomRight: normalizePoint(quad.bottomRight, videoWidth, videoHeight),
      topLeft: normalizePoint(quad.topLeft, videoWidth, videoHeight),
      topRight: normalizePoint(quad.topRight, videoWidth, videoHeight),
    };
  };

  /**
   * Logs optional client extractions, then pushes user to the next step.
   */
  const handleMicroblinkCapture = async (
    params: GovernmentIdMicroblinkTriggerParams,
    captureMethod: CaptureMethod,
    cameraInspectionData: CameraInspectionData | null,
  ) => {
    if (isCaptured.current) {
      return;
    }

    const clientExtractions = [];
    if (requiresSide === GovernmentIdSide.Back && allowIncompleteBarcode) {
      const microblinkResult = params.microblinkResult as BlinkIdSingleSideRecognizerResult;
      if (microblinkResult) {
        clientExtractions.push({
          type: 'pdf417',
          value: microblinkResult.barcode?.barcodeData?.stringData,
        });
        if (params.detectedQuad) {
          const normalizedQuad = normalizeQuad(
            params.detectedQuad,
            params.videoWidth,
            params.videoHeight,
          );
          clientExtractions.push({
            type: 'card',
            value: {
              back: {
                'top-left': normalizedQuad.topLeft,
                'top-right': normalizedQuad.topRight,
                'bottom-left': normalizedQuad.bottomLeft,
                'bottom-right': normalizedQuad.bottomRight,
              },
            },
          });
        }
      }
    } else if (
      requiresSide === GovernmentIdSide.BarcodePdf417 ||
      requiresSide === GovernmentIdSide.Back
    ) {
      const microblinkResult = params.microblinkResult as IdBarcodeRecognizerResult;
      if (microblinkResult) {
        clientExtractions.push({
          type: 'pdf417',
          value: microblinkResult.barcodeData?.stringData,
        });
        if (params.detectedQuad) {
          const normalizedQuad = normalizeQuad(
            params.detectedQuad,
            params.videoWidth,
            params.videoHeight,
          );
          clientExtractions.push({
            type: 'card',
            value: {
              // the returned quad is for the full backside, not just the barcode, so store the quad under a different key
              // this shouldn't impact backend because client-side quads are not used by verifications backend
              [requiresSide === GovernmentIdSide.Back && requireFullBack ? 'back' : 'barcode']: {
                'top-left': normalizedQuad.topLeft,
                'top-right': normalizedQuad.topRight,
                'bottom-left': normalizedQuad.bottomLeft,
                'bottom-right': normalizedQuad.bottomRight,
              },
            },
          });
        }
      }
    } else if (requiresSide === GovernmentIdSide.Front) {
      const microblinkResult = params.microblinkResult as BlinkIdSingleSideRecognizerResult;
      if (microblinkResult) {
        clientExtractions.push({
          type: 'viz',
          value: {
            'name-first': microblinkResult.firstName,
            'name-last': microblinkResult.lastName,
            'identification-number': microblinkResult.documentNumber,
            address: microblinkResult.address,
            birthdate: microblinkResult.dateOfBirth.originalString,
            'expiration-date': microblinkResult.dateOfExpiry.originalString,
          },
        });
        if (governmentIdClass === 'pp') {
          const microblinkMrz = microblinkResult.mrz as MrzResult;
          clientExtractions.push({
            type: 'mrz',
            value: microblinkMrz.rawMRZString,
          });
        }
        if (params.detectedQuad) {
          const normalizedQuad = normalizeQuad(
            params.detectedQuad,
            params.videoWidth,
            params.videoHeight,
          );
          clientExtractions.push({
            type: 'card',
            value: {
              front: {
                'top-left': normalizedQuad?.topLeft,
                'top-right': normalizedQuad?.topRight,
                'bottom-left': normalizedQuad?.bottomLeft,
                'bottom-right': normalizedQuad?.bottomRight,
              },
            },
          });
        }
        clientExtractions.push({
          type: 'classification',
          value: {
            type: microblinkResult.classInfo.documentType
              ? documentTypeToString(microblinkResult.classInfo.documentType)
              : null,
            country: microblinkResult.classInfo.country
              ? countryToString(microblinkResult.classInfo.country)
              : null,
            region: microblinkResult.classInfo.region
              ? regionToString(microblinkResult.classInfo.region)
              : null,
          },
        });
      }
    }
    if (reportDebugMetrics) {
      EventsClient.logAmplitude('government-id.microblink.capture', {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        processingTime: params.processingTime / 1000,
        captureTime:
          initializationTime != null ? (performance.now() - initializationTime) / 1000 : undefined,
        side: requiresSide,
        idClass: governmentIdClass,
        captureMethod,
        parsedViz: clientExtractions.find((e) => e.type === 'viz') != null,
        parsedMrz: clientExtractions.find((e) => e.type === 'mrz') != null,
        parsedCard: clientExtractions.find((e) => e.type === 'card') != null,
        parsedClassification: clientExtractions.find((e) => e.type === 'classification') != null,
        parsedPdf417: clientExtractions.find((e) => e.type === 'pdf417') != null,
      });
    }
    await handleCapture(
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      [params.imageDataUri],
      captureMethod,
      cameraInspectionData,
      clientExtractions,
      params.microblinkRecognizerName,
    );
    isCaptured.current = true;
  };

  const triggerConfigs =
    requiresSide === GovernmentIdSide.Back && allowIncompleteBarcode
      ? TRIGGER_CONFIGS_BY_SIDE[GovernmentIdSide.Front] // if allowing for incomplete barcode extraction, dimensions of quads should match ID Front
      : requiresSide === GovernmentIdSide.Back && requireFullBack
        ? FULL_BACK_SIDE_TRIGGER_CONFIGS
        : TRIGGER_CONFIGS_BY_SIDE[requiresSide];

  return (
    <BaseScanner<
      GovernmentIdMicroblinkTriggerType,
      GovernmentIdMicroblinkTriggerParams,
      StepGovernmentIdConfig['localizations']['capture-page']
    >
      // NOTE: this does not use imageSrcs from BaseScanner.captureClient at all
      handleCapture={({ captureMethod, params, cameraInspectionData }) =>
        handleMicroblinkCapture(params, captureMethod, cameraInspectionData)
      }
      handleFailed={handleError}
      detectInterval={DETECT_INTERVAL}
      labelTakePhoto={labelTakePhoto}
      videoConstraints={videoConstraints}
      triggerConfigs={triggerConfigs}
      paramsFromVideoFrame={paramsFromVideoFrame}
      shouldSubmit={shouldSubmit}
      hasValidQuad={(params) => hasValidQuad(requiresSide, params, requireFullBack)}
      renderOverlay={renderOverlay}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      handleTrigger={handleTrigger}
      delayUntilManualCaptureMs={delayUntilManualCaptureMs}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      triggerOverride={isSlowProcessing ? PROCESSING_TRIGGER : null}
      shouldDisplayCameraSelector={shouldDisplayCameraSelector}
      cameraSelectOptions={cameraSelectOptions}
      selectedCamera={selectedCamera}
      handleCameraChange={handleCameraChange}
      overlayReady={overlayReady}
      overlayRef={overlayRef}
      overlayCaptureWindowRef={overlayCaptureWindowRef}
      previewViewfinderRef={previewViewfinderRef}
      isShortBarcodeOverlay={isShortBarcodeOverlay}
      submitButtonStyles={submitButtonStyles}
      showHelpButton={showHelpButton}
      helpButtonIconColor={helpButtonIconColor}
      helpButtonLabel={helpButtonLabel}
      renderAutoCapturePill={renderAutoCapturePill}
      renderHelpModal={renderHelpModal}
      scannerType='microblink'
      uiReady={uiReady}
      videoReady={videoReady}
      disableCaptureClient
      // additional props (in addition to requiresSide and governmentIdSide) to pass to BaseScanner to render overlay inline
      assets={assets}
      captureOverlay={captureOverlay}
      captureFrameLabel={captureFrameLabel}
      hint={hint}
      prompt={prompt}
      requiresSide={requiresSide}
      governmentIdClass={governmentIdClass}
    />
  );
};

export default GovernmentIdMicroblinkScanner;
